import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Truck, Calendar, User, Package, Printer } from 'lucide-react';
import ConsignmentEntryFormLT from './ConsignmentEntryFormLT';
import { printConsignment } from '../utils/printUtils';
import apiClient from '../services/api';
import '../styles/ConsignmentList.css';

const ConsignmentLT = () => {
  const [consignments, setConsignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Get session data
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();

  useEffect(() => {
    loadConsignments();
  }, []);

  const loadConsignments = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('📡 Loading LT consignments...');
      
      const response = await apiClient.get('/ConsignmentsLT.php');
      
      console.log('📦 Response:', response.data);
      
      if (response.data.success) {
        const consignments = response.data.data.consignments || [];
        setConsignments(consignments);
        console.log('✅ Loaded', consignments.length, 'consignments');
      } else {
        throw new Error(response.data.message || 'Failed to load');
      }
    } catch (err) {
      console.error('❌ Error loading consignments:', err);
      
      let errorMessage = 'Failed to load consignments';
      
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = 'Session expired. Please login again.';
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        } else {
          errorMessage = err.response.data?.message || errorMessage;
        }
      } else if (err.request) {
        errorMessage = 'Server not responding. Please check if backend is running.';
      } else {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditData(null);
    setShowForm(true);
  };
 
  // Updated handleSave and handleDelete functions for ConsignmentLT.jsx
// Replace these functions in your ConsignmentLT.jsx:

const handleSave = async (formData) => {
  try {
    console.log('💾 Saving consignment:', formData);
    
    let response;
    
    // Check if we're updating (has ConsignmentID) or creating (no ConsignmentID)
    if (formData.ConsignmentID) {
      // UPDATE - use PUT
      console.log('📝 Updating consignment ID:', formData.ConsignmentID);
      response = await apiClient.put('/ConsignmentsLT.php', formData);
      
      if (response.data.success) {
        console.log('✅ Updated successfully:', response.data.data.ConsignmentNo);
        alert(`Consignment updated successfully: ${response.data.data.ConsignmentNo}`);
      }
    } else {
      // CREATE - use POST
      console.log('➕ Creating new consignment');
      response = await apiClient.post('/ConsignmentsLT.php', formData);
      
      if (response.data.success) {
        console.log('✅ Created successfully:', response.data.data.consignmentNo);
        alert(`Consignment created: ${response.data.data.consignmentNo}`);
      }
    }
    
    if (!response.data.success) {
      throw new Error(response.data.message);
    }
    
    // Close form and reload list
    setShowForm(false);
    setEditData(null);
    loadConsignments();
    
  } catch (err) {
    console.error('❌ Error saving:', err);
    const errorMsg = err.response?.data?.message || err.message;
    alert('Error saving consignment: ' + errorMsg);
    throw new Error(errorMsg);
  }
};

const handleDelete = async (id) => {
  if (!window.confirm('Are you sure you want to delete this consignment?')) {
    return;
  }

  try {
    console.log('🗑️ Deleting consignment ID:', id);
    
    const response = await apiClient.delete(`/ConsignmentsLT.php/${id}`);
    
    if (response.data.success) {
      console.log('✅ Deleted successfully');
      alert('Consignment deleted successfully!');
      loadConsignments();
    } else {
      alert('Error: ' + response.data.message);
    }
  } catch (err) {
    console.error('❌ Error deleting:', err);
    alert('Error deleting consignment: ' + (err.response?.data?.message || err.message));
  }
};

// Also update handleEdit to load full consignment data:
const handleEdit = async (consignment) => {
  try {
    console.log('📝 Loading consignment for edit:', consignment.ConsignmentID);
    
    // Fetch full consignment details
    const response = await apiClient.get(`/ConsignmentsLT.php/${consignment.ConsignmentID}`);
    
    if (response.data.success) {
      setEditData(response.data.data);
      setShowForm(true);
    } else {
      alert('Error loading consignment: ' + response.data.message);
    }
  } catch (err) {
    console.error('Error loading consignment:', err);
    alert('Error loading consignment: ' + (err.response?.data?.message || err.message));
  }
};
 
  // ✨ NEW: Handle print
  const handlePrint = async (consignment) => {
    try {
      console.log('🖨️ Printing consignment:', consignment.ConsignmentNo);
      
      // Fetch full details if needed (optional - depends on your API)
      // For now, we'll print with the data we have
      printConsignment(consignment, 'LT');
      
    } catch (err) {
      console.error('❌ Error printing:', err);
      alert('Error printing consignment: ' + err.message);
    }
  };

   

  const handleClose = () => {
    setShowForm(false);
    setEditData(null);
  };

  // Check session
  if (!session?.companyid) {
    return (
      <div className="consignment-page">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  const filteredConsignments = consignments.filter(c =>
    c.ConsignmentNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.ShipperName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.ConsigneeName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="consignment-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <div className="header-icon">
            <Truck size={28} />
          </div>
          <div>
            <h1>Land Transport Consignments</h1>
            <p className="header-subtitle">Manage LT consignments and shipments</p>
          </div>
        </div>
        <button className="btn-primary" onClick={handleNew}>
          <Plus size={20} />
          New Consignment
        </button>
      </div>

      {/* Toolbar */}
      <div className="page-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by consignment no, shipper, or consignee..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          <span className="count-badge">{filteredConsignments.length}</span>
          {filteredConsignments.length === 1 ? 'consignment' : 'consignments'}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading consignments...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
          <button className="btn-primary" onClick={loadConsignments}>
            Retry
          </button>
        </div>
      ) : filteredConsignments.length === 0 ? (
        <div className="empty-state">
          <Truck size={48} style={{ opacity: 0.3 }} />
          <h3>No consignments found</h3>
          <p>
            {searchTerm 
              ? 'Try adjusting your search terms' 
              : 'Get started by creating your first consignment'}
          </p>
          {!searchTerm && (
            <button className="btn-primary" onClick={handleNew}>
              <Plus size={20} />
              New Consignment
            </button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Consignment No</th>
                <th>Date</th>
                <th>Shipper</th>
                <th>Consignee</th>
                <th>Origin → Destination</th>
                <th>Weight (kg)</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredConsignments.map(consignment => (
                <tr key={consignment.ConsignmentID}>
                  <td className="font-mono">{consignment.ConsignmentNo}</td>
                  <td>
                    <div className="date-cell">
                      <Calendar size={14} />
                      {new Date(consignment.ConsignmentDate).toLocaleDateString()}
                    </div>
                  </td>
                  <td>
                    <div className="customer-cell">
                      <User size={14} />
                      <div>
                        <div className="customer-name">{consignment.ShipperName || '-'}</div>
                        {consignment.ShipperCode && (
                          <div className="customer-code">{consignment.ShipperCode}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="customer-cell">
                      <User size={14} />
                      <div>
                        <div className="customer-name">{consignment.ConsigneeName || '-'}</div>
                        {consignment.ConsigneeCode && (
                          <div className="customer-code">{consignment.ConsigneeCode}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="route-cell">
                      {consignment.ShipperCity || 'N/A'} → {consignment.ConsigneeCity || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="weight-cell">
                      <Package size={14} />
                      {consignment.Weight ? parseFloat(consignment.Weight).toFixed(2) : '0.00'}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge status-${consignment.StatusColor || 'default'}`}>
                      {consignment.Status || 'Pending'}
                    </span>
                  </td>
                  <td className="actions-column">
                    {/* ✨ NEW: Print Button */}
                    <button
                      className="btn-icon btn-print"
                      onClick={() => handlePrint(consignment)}
                      title="Print Consignment"
                    >
                      <Printer size={16} />
                    </button>
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(consignment)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(consignment.ConsignmentID)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <ConsignmentEntryFormLT
          onClose={handleClose}
          onSave={handleSave}
          editData={editData}
        />
      )}

      {/* ✨ Add custom styles for print button */}
      <style jsx>{`
        .btn-print {
          background-color: #3b82f6;
          color: white;
        }
        
        .btn-print:hover {
          background-color: #2563eb;
        }
      `}</style>
    </div>
  );
};

export default ConsignmentLT;